import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import { DatePipe, NgClass } from '@angular/common';
import { Component, effect, input } from '@angular/core';
import { AuditLog } from '@shared/types';
import { HlmButtonDirective } from '@spartan-ng/button';
import { HlmCardModule } from '@spartan-ng/card';
import { HlmIconComponent } from '@spartan-ng/icon';

/**
 * @component CustomerCaseAuditTrailComponent
 * @description Displays a timeline of audit events for a customer case, showing changes and updates
 * in a chronological format with expandable details for complex changes.
 */
@Component({
  selector: 'dxc-audit-timeline',
  imports: [
    NgClass,
    DatePipe,
    HlmCardModule,
    HlmIconComponent,
    HlmButtonDirective
  ],
  templateUrl: './audit-timeline.component.html',
  styles: [
    `
      :host {
        display: block;
      }
    `
  ],
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({ height: '0', opacity: '0' })),
      state('expanded', style({ height: '*', opacity: '1' })),
      transition('collapsed <=> expanded', [
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)')
      ])
    ]),
    trigger('expandCollapseChanges', [
      state('collapsed', style({ height: '0', opacity: '0' })),
      state('expanded', style({ height: '*', opacity: '1' })),
      transition('collapsed <=> expanded', [
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)')
      ])
    ])
  ]
})
export class AuditTimelineComponent {
  auditLogs = input.required<AuditLog[]>();
  isCollapsible = input(false);
  inPdf = input(false);
  expandedChanges = new Set<string>();
  expandedItems = new Set<string>();

  constructor() {
    effect(() => {
      // Reset and initialize expanded items when inputs change
      this.expandedItems.clear();
      this.expandedChanges.clear();

      if (
        (this.isCollapsible() || this.inPdf())
        && this.auditLogs()?.length > 0
      ) {
        this.expandedItems.add(this.auditLogs()[0].timestamp);

        // If in PDF mode, expand all changes
        if (this.inPdf()) {
          this.auditLogs().forEach((item) => {
            item.details?.forEach((detail, index) => {
              const changeId = this.generateChangeId(
                item.timestamp,
                index,
                detail.field
              );
              this.expandedChanges.add(changeId);
            });
          });
        }
      }
    });
  }

  /**
   * Toggles the expansion state of a timeline item
   * @param timestamp Unique identifier for the timeline item
   * @param event The mouse event
   */
  protected toggleItemExpansion(timestamp: string, event: MouseEvent): void {
    // Don't toggle if clicking on a button or its children
    if (
      event.target instanceof Element
      && (event.target.closest('button') || !this.isCollapsible())
    ) {
      return;
    }

    if (this.expandedItems.has(timestamp)) {
      this.expandedItems.delete(timestamp);
    } else {
      this.expandedItems.add(timestamp);
    }
  }

  /**
   * Checks if a timeline item is expanded
   * @param timestamp Unique identifier for the timeline item
   * @returns Boolean indicating if the item is expanded
   */
  protected isItemExpanded(timestamp: string): boolean {
    return !this.isCollapsible() || this.expandedItems.has(timestamp);
  }

  /**
   * Converts an action type to its past tense form for display
   * @param action The action type (e.g., 'create', 'update', 'delete')
   * @returns The past tense form of the action (e.g., 'created', 'updated', 'deleted')
   */
  protected getActionVerb(action: string): string {
    switch (action.toLowerCase()) {
      case 'create':
        return 'created';
      case 'update':
        return 'updated';
      case 'delete':
        return 'deleted';
      default:
        return action;
    }
  }

  /**
   * Generates a human-readable description of the audit log event
   * @param item The audit log entry
   * @returns A formatted string describing the audit event (e.g., "Case #123 was updated")
   */
  protected getTimelineContent(item: AuditLog): string {
    const action = this.getActionVerb(item.action);
    return `${item.object_repr} was ${action}`;
  }

  /**
   * Determines the background color for the timeline icon based on the action type
   * @param action The action type (e.g., 'create', 'update', 'delete')
   * @returns A Tailwind CSS class for the background color
   */
  protected getIconColor(action: string): string {
    switch (action.toLowerCase()) {
      case 'create':
        return 'bg-green-500';
      case 'update':
        return 'bg-blue-500';
      case 'delete':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  }

  /**
   * Gets the appropriate icon name for the action type
   * @param action The action type (e.g., 'create', 'update', 'delete')
   * @returns The Lucide icon name to display
   */
  protected getIconName(action: string): string {
    switch (action.toLowerCase()) {
      case 'create':
        return 'lucidePlus';
      case 'update':
        return 'lucidePencil';
      case 'delete':
        return 'lucideTrash2';
      default:
        return 'lucideInfo';
    }
  }

  /**
   * Generates a unique ID for a change entry
   * @param timestamp The audit log timestamp
   * @param index The detail index
   * @param field The field name
   * @returns A unique identifier string
   */
  protected generateChangeId(
    timestamp: string,
    index: number,
    field: string
  ): string {
    return `${timestamp}_${index}_${field}`;
  }

  /**
   * Toggles the expansion state of a change detail section
   * @param changeId Unique identifier for the change
   */
  protected toggleChangeExpansion(changeId: string): void {
    if (this.expandedChanges.has(changeId)) {
      this.expandedChanges.delete(changeId);
    } else {
      this.expandedChanges.add(changeId);
    }
  }

  /**
   * Checks if a specific change detail section is expanded
   * @param changeId Unique identifier for the change
   * @returns Boolean indicating if the section is expanded
   */
  protected isChangeExpanded(changeId: string): boolean {
    return this.inPdf() || this.expandedChanges.has(changeId);
  }

  /**
   * Attempts to parse a string as JSON, returns original string if parsing fails
   * @param change The string to parse
   * @returns Parsed object or original string
   */
  protected formatChange(change: string): string | object {
    try {
      return JSON.parse(change);
    } catch {
      return change;
    }
  }

  /**
   * Compares old and new JSON values to find differences
   * @param oldValue The original JSON string
   * @param newValue The updated JSON string
   * @returns Array of objects containing the key and its old/new values for changed fields
   */
  protected getChangeDifferences(
    oldValue: string,
    newValue: string
  ): { key: string; old: unknown; new: unknown }[] {
    try {
      const oldObj = JSON.parse(oldValue);
      const newObj = JSON.parse(newValue);

      return Object.keys(oldObj).reduce(
        (acc, key) => {
          if (oldObj[key] !== newObj[key]) {
            acc.push({ key, old: oldObj[key], new: newObj[key] });
          }
          return acc;
        },
        [] as { key: string; old: unknown; new: unknown }[]
      );
    } catch {
      return [];
    }
  }

  /**
   * Checks if a string is a valid JSON object (not an array, null, or primitive)
   * @param str The string to check
   * @returns Boolean indicating if the string represents a JSON object
   */
  protected isJsonString(str: string): boolean {
    try {
      const parsed = JSON.parse(str);
      // Only return true if the parsed value is an object (not null) or array
      return (
        typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      );
    } catch {
      return false;
    }
  }
}
